// Notification utilities for Beypointer PWA

export const requestNotificationPermission = async () => {
  console.log('Requesting notification permission...');
  
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  console.log('Current notification permission:', Notification.permission);

  if (Notification.permission === 'granted') {
    console.log('Notification permission already granted');
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    console.log('Notification permission response:', permission);
    return permission === 'granted';
  }

  console.log('Notification permission denied');
  return false;
};

export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
        updateViaCache: 'none' // Always check for updates
      });
      
      console.log('[APP] Service Worker registered:', registration);
      
      // Removed auto-update checks to prevent disruptive page reloads
      // Service worker will update on natural page refreshes instead
      
      // Listen for service worker updates (without auto-reload)
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('[APP] New service worker found, installing...');
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[APP] New service worker installed, will activate on next page load');
          }
        });
      });
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[APP] Service worker updated:', event.data.message);
        }
      });
      
      // Register periodic background sync for badge updates (if supported)
      if ('periodicSync' in registration) {
        try {
          registration.periodicSync.register('update-badge', {
            minInterval: 60 * 1000 // Check every minute when in background
          }).then(() => {
            console.log('[APP] Periodic background sync registered for badge updates');
          }).catch(err => {
            console.log('[APP] Periodic sync registration failed:', err);
          });
        } catch (err) {
          console.log('[APP] Periodic sync not supported:', err);
        }
      } else {
        console.log('[APP] Periodic Background Sync API not supported');
      }
      
      return registration;
    } catch (error) {
      console.error('[APP] Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

export const showNotification = async (title, options) => {
  console.log('[Notification] ========== START ==========');
  console.log('[Notification] Showing notification:', title);
  console.log('[Notification] Options:', options);
  console.log('[Notification] Permission:', Notification.permission);
  console.log('[Notification] ServiceWorker available:', 'serviceWorker' in navigator);
  console.log('[Notification] Document hidden:', document.hidden);
  
  // Check if notification permission is granted
  if (Notification.permission !== 'granted') {
    console.log('[Notification] ❌ Permission not granted, aborting');
    return;
  }

  // INSTANT notification - no delays
  // Works in both foreground AND background, even on Messages page
  if ('serviceWorker' in navigator) {
    try {
      console.log('[Notification] Waiting for service worker ready...');
      const registration = await navigator.serviceWorker.ready;
      console.log('[Notification] ✓ Service worker ready:', registration);
      
      const notificationOptions = {
        body: `${options.sender}: ${options.message}`,
        icon: '/beypointer-thumbnail.jpg',
        badge: '/beypointer-thumbnail.jpg',
        tag: options.chatId,
        requireInteraction: false,
        silent: false, // Let system play default sound
        vibrate: [200, 100, 200], // Vibration pattern
        data: {
          chatId: options.chatId,
          url: window.location.origin
        }
      };
      console.log('[Notification] Notification options:', notificationOptions);
      
      // Play custom sound FIRST (more reliable)
      console.log('[Notification] Attempting to play custom sound...');
      try {
        const audio = new Audio('/message-notification.mp3');
        audio.volume = 0.8;
        // Try to play immediately (works if user has interacted with page)
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log('[Notification] ✅ Custom sound played successfully');
          }).catch((err) => {
            console.log('[Notification] ⚠️ Custom sound blocked (autoplay policy):', err.message);
          });
        }
      } catch (err) {
        console.log('[Notification] ⚠️ Custom sound error:', err.message);
      }
      
      // Trigger vibration manually (more reliable than notification API)
      if ('vibrate' in navigator) {
        try {
          navigator.vibrate([200, 100, 200]);
          console.log('[Notification] ✅ Vibration triggered');
        } catch (err) {
          console.log('[Notification] ⚠️ Vibration failed:', err.message);
        }
      } else {
        console.log('[Notification] ⚠️ Vibration API not supported');
      }
      
      // Show notification via service worker
      console.log('[Notification] Calling registration.showNotification...');
      await registration.showNotification(title, notificationOptions);
      console.log('[Notification] ✅ Notification shown successfully via service worker!');
    } catch (error) {
      console.error('[Notification] ❌ Service worker error:', error);
      console.error('[Notification] Error stack:', error.stack);
      
      // Fallback to regular notification
      console.log('[Notification] Trying fallback notification...');
      try {
        // Play sound for fallback too
        try {
          const audio = new Audio('/message-notification.mp3');
          audio.volume = 0.8;
          audio.play().catch(e => console.log('[Notification] Fallback sound blocked:', e.message));
        } catch (e) {
          console.log('[Notification] Fallback sound error:', e.message);
        }
        
        // Trigger vibration
        if ('vibrate' in navigator) {
          navigator.vibrate([200, 100, 200]);
        }
        
        const notification = new Notification(title, {
          body: `${options.sender}: ${options.message}`,
          icon: '/beypointer-thumbnail.jpg',
          badge: '/beypointer-thumbnail.jpg',
          tag: options.chatId,
          vibrate: [200, 100, 200]
        });
        console.log('[Notification] ✓ Shown via fallback');
        setTimeout(() => notification.close(), 5000);
      } catch (fallbackError) {
        console.error('[Notification] ❌ Fallback error:', fallbackError);
      }
    }
  } else {
    // Fallback for browsers without service worker
    console.log('[Notification] No service worker, using direct notification API');
    try {
      // Play sound
      try {
        const audio = new Audio('/message-notification.mp3');
        audio.volume = 0.8;
        audio.play().catch(e => console.log('[Notification] Direct API sound blocked:', e.message));
      } catch (e) {
        console.log('[Notification] Direct API sound error:', e.message);
      }
      
      // Trigger vibration
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
        console.log('[Notification] ✓ Vibration triggered (direct API)');
      }
      
      const notification = new Notification(title, {
        body: `${options.sender}: ${options.message}`,
        icon: '/beypointer-thumbnail.jpg',
        badge: '/beypointer-thumbnail.jpg',
        vibrate: [200, 100, 200]
      });
      console.log('[Notification] ✓ Shown via direct API');
      setTimeout(() => notification.close(), 5000);
    } catch (error) {
      console.error('[Notification] ❌ Direct API error:', error);
    }
  }
  console.log('[Notification] ========== END ==========');
};

export const checkNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

export const testNotification = async () => {
  console.log('Testing notification...');
  await showNotification('Test Notification', {
    sender: 'System',
    message: 'This is a test message!',
    chatId: 'test'
  });
};

// Clear all caches and unregister service worker (for debugging/reset)
// Clear all caches and unregister service worker (for debugging/reset)
export const clearAllCaches = async () => {
  try {
    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('[APP] All caches cleared');
    
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      console.log('[APP] All service workers unregistered');
    }
    
    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();
    console.log('[APP] Storage cleared');
    
    return true;
  } catch (error) {
    console.error('[APP] Error clearing caches:', error);
    return false;
  }
};

// Emergency recovery - clears everything and reloads
export const emergencyRecover = async () => {
  console.log('[RECOVERY] Starting emergency recovery...');
  try {
    // Save token if exists
    const token = localStorage.getItem('token');
    const tokenTimestamp = localStorage.getItem('token_timestamp');
    
    // Clear everything
    await clearAllCaches();
    
    // Restore token
    if (token) {
      localStorage.setItem('token', token);
      if (tokenTimestamp) {
        localStorage.setItem('token_timestamp', tokenTimestamp);
      }
    }
    
    console.log('[RECOVERY] Recovery complete, reloading...');
    window.location.href = '/';
    return true;
  } catch (error) {
    console.error('[RECOVERY] Emergency recovery failed:', error);
    // Force reload anyway
    window.location.href = '/';
    return false;
  }
};

// Make recovery function globally available for debugging
if (typeof window !== 'undefined') {
  window.emergencyRecover = emergencyRecover;
}
